import { modals } from "@mantine/modals";
import { StrictUndef } from "shared/api/httpClient";
import { createInfiniteRepository } from "shared/api/infinite-repository";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { FullUserFragment, UsersSearchRepositoryQuery } from "src/gql/graphql";
import { container, singleton } from "tsyringe";

export type UserSchema = UsersSearchRepositoryQuery["users"][number];

@singleton()
export class UsersSearchRepository extends createInfiniteRepository(
    graphql(`
        query UsersSearchRepository(
            $limit: Int!
            $offset: Int!
            $nameQuery: String
            $groupId: UUID
            $groupless: Boolean
            $roleId: UUID
            $status: UserStatus
        ) {
            users(
                limit: $limit
                offset: $offset
                nameQuery: $nameQuery
                groupId: $groupId
                groupless: $groupless
                roleId: $roleId
                status: $status
            ) {
                ...FullUser
            }
        }
    `),
    {
        page: (doc) => doc.users,
    },
) {}

@singleton()
export class OptInUsersSearchRepository extends createInfiniteRepository(
    graphql(`
        query OptInUsersSearchRepository($offset: Int!, $limit: Int!, $filters: OptInUserSearchParametersInput) {
            optInUsers(offset: $offset, limit: $limit, filters: $filters) {
                id
                name
                euds {
                    id
                    value
                    isVerified
                }
                statusSince
                expiration
            }
        }
    `),
    {
        page: (doc) => doc.optInUsers,
        sort: { by: (it) => it.statusSince, ord: "desc" },
    },
) {}

@singleton()
export class OrganizationRolesRepository extends createInfiniteRepository(
    graphql(`
        query OrganizationRoles($limit: Int!, $offset: Int!) {
            roles(limit: $limit, offset: $offset) {
                id
                name
                type
            }
        }
    `),
    { page: (doc) => doc.roles },
) {}

export function handleRoleAssign(promises: Promise<StrictUndef<FullUserFragment>>[]) {
    async function invalidateAndCloseModal(users: StrictUndef<FullUserFragment>[]) {
        await qk.invalidate("user");
        const repo = container.resolve(UsersSearchRepository);
        users.forEach((user) => repo.upsert(user));
        modals.closeAll();
    }

    return Promise.allSettled(promises).then(async (results) => {
        const successes = results.filter((res) => res.status == "fulfilled").flatMap((it) => it.value);
        await invalidateAndCloseModal(successes);
        if (results.length == successes.length) notify.show.success({ message: "Roles updated" });
        else if (successes.length == 0) notify.show.error({ message: "Roles could not be updated" });
        else notify.show.warn({ message: `${results.length - successes.length} user(s) could not be updated` });
    });
}
